import { useEffect, useRef, useState, useCallback } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { DOMParser as PMDomParser } from "@tiptap/pm/model";
import axios from "axios";
import {
    Bold,
    Italic,
    Strikethrough,
    Heading1,
    Heading2,
    Heading3,
    List,
    ListOrdered,
    Quote,
    Undo2,
    Redo2,
    Link as LinkIcon,
    Minus,
    Pilcrow,
    ImagePlus,
    Table as TableIcon,
    Search,
    ChevronUp,
    ChevronDown,
    X,
    Replace,
} from "lucide-react";
import {
    SearchAndReplace,
    setSearchQuery,
    clearSearch,
    stepMatch,
    getSearchState,
    replaceCurrent,
    replaceAll,
} from "@/components/editor/SearchAndReplace";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Escape HTML entities for safe insertion into rendered structure.
const esc = (s) =>
    (s || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

/**
 * Convert plain-text pasted from a PDF or webpage into structured HTML:
 * - Blank lines separate paragraphs.
 * - Lines that all start with a bullet marker → a <ul>.
 * - Consecutive lines starting with "1.", "2.", … → an <ol>.
 * - Roman-numeral prefixed short lines → <h2>.
 * - Numeric-prefixed short capitalised lines → <h3>.
 * - Multi-tab / >=2-space column layouts inside a group of lines → <table>.
 */
export function smartPlainTextToHtml(raw) {
    // Normalise line endings and strip trailing spaces
    const text = raw.replace(/\r\n?/g, "\n").replace(/[ \t]+\n/g, "\n");
    // Split into blocks by blank lines
    const blocks = text.split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
    if (blocks.length === 0) return "";

    const romanRe = /^\s*[IVXLC]+\.\s+\S/;
    const numRe = /^\s*\d+\.\s+/;
    const bulletRe = /^\s*(?:[•·▪◦●○]|-|—|–|\*|e)\s+/;

    const out = [];
    for (const block of blocks) {
        const lines = block.split("\n").map((l) => l.trimEnd());

        // Tab-separated table (2+ lines, each with ≥1 tab or ≥2 spaces between fields)
        const looksTabular =
            lines.length >= 2
            && lines.every((l) => l.includes("\t") || /\S {2,}\S/.test(l));
        if (looksTabular) {
            const rows = lines.map((l) => l.split(/\t|\s{2,}/).map((c) => c.trim()));
            const cols = rows[0].length;
            if (cols >= 2 && rows.every((r) => r.length === cols)) {
                const head = rows[0];
                const body = rows.slice(1);
                out.push(
                    `<table><thead><tr>${head
                        .map((c) => `<th>${esc(c)}</th>`)
                        .join("")}</tr></thead><tbody>${body
                        .map(
                            (r) =>
                                `<tr>${r
                                    .map((c) => `<td>${esc(c)}</td>`)
                                    .join("")}</tr>`,
                        )
                        .join("")}</tbody></table>`,
                );
                continue;
            }
        }

        // Bulleted list
        if (lines.length >= 2 && lines.every((l) => bulletRe.test(l))) {
            const items = lines.map((l) => l.replace(bulletRe, "").trim());
            out.push(
                `<ul>${items.map((i) => `<li>${esc(i)}</li>`).join("")}</ul>`,
            );
            continue;
        }

        // Ordered list
        if (lines.length >= 2 && lines.every((l) => numRe.test(l))) {
            const items = lines.map((l) => l.replace(numRe, "").trim());
            out.push(
                `<ol>${items.map((i) => `<li>${esc(i)}</li>`).join("")}</ol>`,
            );
            continue;
        }

        // Heading detection on a single-line block
        if (lines.length === 1) {
            const single = lines[0];
            const words = single.split(/\s+/).length;
            if (words <= 15 && romanRe.test(single)) {
                out.push(`<h2>${esc(single)}</h2>`);
                continue;
            }
            if (
                words <= 15
                && numRe.test(single)
                && /^\s*\d+\.\s+[A-Z]/.test(single)
            ) {
                out.push(`<h3>${esc(single)}</h3>`);
                continue;
            }
        }

        // Paragraph — merge soft line-wraps within the block into one flow
        const joined = lines.join(" ").replace(/\s+/g, " ").trim();
        out.push(`<p>${esc(joined)}</p>`);
    }
    return out.join("");
}

const ToolButton = ({ onClick, active, disabled, label, testId, children }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        aria-pressed={active}
        title={label}
        data-testid={testId}
        className={`inline-flex items-center justify-center h-8 w-8 border transition-colors ${
            active
                ? "bg-[color:var(--charcoal)] text-white border-[color:var(--charcoal)]"
                : "border-[color:var(--charcoal)]/25 hover:bg-[color:var(--charcoal)]/5"
        } disabled:opacity-30 disabled:cursor-not-allowed`}
    >
        {children}
    </button>
);

export default function RichTextEditor({ value, onChange, placeholder, token }) {
    const fileInputRef = useRef(null);
    const uploadingRef = useRef(false);
    const [findOpen, setFindOpen] = useState(false);
    const [findMode, setFindMode] = useState("find"); // "find" | "replace"
    const [query, setQuery] = useState("");
    const [replaceText, setReplaceText] = useState("");
    const [caseSensitive, setCaseSensitive] = useState(false);
    // Match counter is re-read from plugin state on every render trigger.
    const [tick, setTick] = useState(0);
    const findInputRef = useRef(null);
    const editorHostRef = useRef(null);
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3] },
                link: {
                    openOnClick: false,
                    HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" },
                },
            }),
            Image.configure({
                inline: false,
                allowBase64: false,
                HTMLAttributes: { class: "rte-image" },
            }),
            Table.configure({ resizable: false, HTMLAttributes: { class: "rte-table" } }),
            TableRow,
            TableHeader,
            TableCell,
            Placeholder.configure({
                placeholder: placeholder || "Paste or edit article content here…",
            }),
            SearchAndReplace,
        ],
        content: value || "",
        onUpdate: ({ editor: e }) => {
            const html = e.getHTML();
            onChange?.(html === "<p></p>" ? "" : html);
        },
        editorProps: {
            attributes: {
                class: "tiptap-editor prose max-w-none font-body text-base leading-[1.7] focus:outline-none min-h-[420px] px-5 py-4",
                "data-testid": "rte-editor",
            },
            handlePaste(view, event) {
                const cd = event.clipboardData;
                if (!cd) return false;
                const html = cd.getData("text/html");
                const text = cd.getData("text/plain");
                // Rich HTML → let Tiptap handle it natively (tables/lists/headings covered).
                if (html && /<(table|ul|ol|h[1-6]|li)[^>]*>/i.test(html)) return false;
                if (!text || text.length < 2) return false;
                const structured = smartPlainTextToHtml(text);
                // If our transform produced nothing useful, fall back to default.
                if (!structured || structured === `<p>${text.trim()}</p>`) return false;
                event.preventDefault();
                // Insert as HTML fragment via ProseMirror's DOMParser.
                const { schema } = view.state;
                const doc = new window.DOMParser().parseFromString(structured, "text/html");
                const slice = PMDomParser.fromSchema(schema).parseSlice(doc.body);
                const tr = view.state.tr.replaceSelection(slice);
                view.dispatch(tr);
                return true;
            },
        },
    });

    // Sync external value when it changes (e.g. after PDF upload sets draft_html)
    useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        if (value !== undefined && value !== null && value !== current) {
            editor.commands.setContent(value || "", { emitUpdate: false });
        }
    }, [value, editor]);

    // Push the current search query into the ProseMirror plugin state so
    // decorations refresh whenever the toolbar changes. Reset activeIdx on
    // every keystroke so the reader lands on the first match again.
    useEffect(() => {
        if (!editor) return;
        if (findOpen && query) {
            setSearchQuery(editor, { query, caseSensitive });
        }
        if (findOpen && !query) {
            clearSearch(editor);
        }
        setTick((t) => t + 1);
    }, [editor, findOpen, query, caseSensitive]);

    // Wipe search state entirely once the bar closes so decorations vanish.
    useEffect(() => {
        if (!editor) return;
        if (!findOpen) clearSearch(editor);
    }, [editor, findOpen]);

    // `Cmd/Ctrl+F` opens the find bar; `Cmd/Ctrl+H` opens with replace
    // visible. Scoped to the editor host so global browser search still
    // works everywhere else on the page.
    const openFind = useCallback((mode) => {
        setFindMode(mode);
        setFindOpen(true);
        requestAnimationFrame(() => {
            findInputRef.current?.focus();
            findInputRef.current?.select();
        });
    }, []);

    useEffect(() => {
        const host = editorHostRef.current;
        if (!host) return;
        const handler = (e) => {
            const mod = e.metaKey || e.ctrlKey;
            if (!mod) return;
            const k = e.key.toLowerCase();
            if (k === "f") {
                e.preventDefault();
                openFind("find");
            } else if (k === "h") {
                e.preventDefault();
                openFind("replace");
            }
        };
        host.addEventListener("keydown", handler);
        return () => host.removeEventListener("keydown", handler);
    }, [openFind]);

    const findState = editor ? getSearchState(editor) : null;
    const totalMatches = findState?.matches?.length || 0;
    const activePosition = totalMatches ? findState.activeIdx + 1 : 0;

    const gotoPrev = () => editor && stepMatch(editor, -1);
    const gotoNext = () => editor && stepMatch(editor, 1);
    const onReplaceOne = () => {
        if (!editor) return;
        replaceCurrent(editor);
        setSearchQuery(editor, { query, caseSensitive, replaceQuery: replaceText });
        setTick((t) => t + 1);
    };
    const onReplaceAll = () => {
        if (!editor || !query) return;
        setSearchQuery(editor, { query, caseSensitive, replaceQuery: replaceText });
        const n = replaceAll(editor);
        if (n > 0) window.setTimeout(() => setTick((t) => t + 1), 30);
    };

    const closeFind = () => setFindOpen(false);

    if (!editor) {
        return (
            <div className="border border-[color:var(--charcoal)]/30 p-6 font-mono text-[13px] uppercase tracking-[0.22em] text-[color:var(--slate)]">
                Loading editor…
            </div>
        );
    }

    const setLink = () => {
        const prev = editor.getAttributes("link").href;
        const url = window.prompt("URL", prev || "https://");
        if (url === null) return;
        if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    };

    const onPickImage = () => fileInputRef.current?.click();

    const onFileChosen = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = ""; // allow re-selecting the same file next time
        if (!f) return;
        if (uploadingRef.current) return;
        uploadingRef.current = true;
        try {
            const form = new FormData();
            form.append("file", f);
            const res = await axios.post(`${API}/admin/upload/image`, form, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            });
            const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
            editor.chain().focus().setImage({ src: url, alt: f.name }).run();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            window.alert(`Image upload failed: ${typeof detail === "string" ? detail : err.message || "unknown error"}`);
        } finally {
            uploadingRef.current = false;
        }
    };

    return (
        <div
            ref={editorHostRef}
            className="border border-[color:var(--charcoal)]/30 bg-white"
            data-testid="rich-text-editor"
        >
            <div
                role="toolbar"
                aria-label="Formatting"
                className="flex flex-wrap gap-1 p-2 border-b border-[color:var(--charcoal)]/15 bg-[color:var(--cloud)]"
            >
                <ToolButton
                    label="Heading 1"
                    testId="rte-h1"
                    active={editor.isActive("heading", { level: 1 })}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                >
                    <Heading1 size={15} strokeWidth={1.7} />
                </ToolButton>
                <ToolButton
                    label="Heading 2"
                    testId="rte-h2"
                    active={editor.isActive("heading", { level: 2 })}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                >
                    <Heading2 size={15} strokeWidth={1.7} />
                </ToolButton>
                <ToolButton
                    label="Heading 3"
                    testId="rte-h3"
                    active={editor.isActive("heading", { level: 3 })}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                >
                    <Heading3 size={15} strokeWidth={1.7} />
                </ToolButton>
                <ToolButton
                    label="Paragraph"
                    testId="rte-p"
                    active={editor.isActive("paragraph") && !editor.isActive("heading")}
                    onClick={() => editor.chain().focus().setParagraph().run()}
                >
                    <Pilcrow size={15} strokeWidth={1.7} />
                </ToolButton>
                <span className="mx-1 w-px bg-[color:var(--charcoal)]/15" aria-hidden />
                <ToolButton
                    label="Bold"
                    testId="rte-bold"
                    active={editor.isActive("bold")}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                >
                    <Bold size={15} strokeWidth={1.7} />
                </ToolButton>
                <ToolButton
                    label="Italic"
                    testId="rte-italic"
                    active={editor.isActive("italic")}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                >
                    <Italic size={15} strokeWidth={1.7} />
                </ToolButton>
                <ToolButton
                    label="Strikethrough"
                    testId="rte-strike"
                    active={editor.isActive("strike")}
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                >
                    <Strikethrough size={15} strokeWidth={1.7} />
                </ToolButton>
                <span className="mx-1 w-px bg-[color:var(--charcoal)]/15" aria-hidden />
                <ToolButton
                    label="Bullet list"
                    testId="rte-ul"
                    active={editor.isActive("bulletList")}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                >
                    <List size={15} strokeWidth={1.7} />
                </ToolButton>
                <ToolButton
                    label="Numbered list"
                    testId="rte-ol"
                    active={editor.isActive("orderedList")}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                >
                    <ListOrdered size={15} strokeWidth={1.7} />
                </ToolButton>
                <ToolButton
                    label="Blockquote"
                    testId="rte-quote"
                    active={editor.isActive("blockquote")}
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                >
                    <Quote size={15} strokeWidth={1.7} />
                </ToolButton>
                <ToolButton
                    label="Horizontal rule"
                    testId="rte-hr"
                    onClick={() => editor.chain().focus().setHorizontalRule().run()}
                >
                    <Minus size={15} strokeWidth={1.7} />
                </ToolButton>
                <ToolButton
                    label="Link"
                    testId="rte-link"
                    active={editor.isActive("link")}
                    onClick={setLink}
                >
                    <LinkIcon size={14} strokeWidth={1.7} />
                </ToolButton>
                <ToolButton
                    label="Insert table"
                    testId="rte-table"
                    onClick={() =>
                        editor
                            .chain()
                            .focus()
                            .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                            .run()
                    }
                >
                    <TableIcon size={14} strokeWidth={1.7} />
                </ToolButton>
                <ToolButton
                    label="Insert image"
                    testId="rte-image"
                    onClick={onPickImage}
                >
                    <ImagePlus size={15} strokeWidth={1.7} />
                </ToolButton>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={onFileChosen}
                    data-testid="rte-image-input"
                    className="hidden"
                />
                <span className="mx-1 w-px bg-[color:var(--charcoal)]/15" aria-hidden />
                <ToolButton
                    label="Undo"
                    testId="rte-undo"
                    disabled={!editor.can().undo()}
                    onClick={() => editor.chain().focus().undo().run()}
                >
                    <Undo2 size={14} strokeWidth={1.7} />
                </ToolButton>
                <ToolButton
                    label="Redo"
                    testId="rte-redo"
                    disabled={!editor.can().redo()}
                    onClick={() => editor.chain().focus().redo().run()}
                >
                    <Redo2 size={14} strokeWidth={1.7} />
                </ToolButton>
                <span className="mx-1 w-px bg-[color:var(--charcoal)]/15" aria-hidden />
                <ToolButton
                    label="Find (⌘F)"
                    testId="rte-find"
                    active={findOpen}
                    onClick={() => openFind(findOpen ? findMode : "find")}
                >
                    <Search size={14} strokeWidth={1.7} />
                </ToolButton>
                <ToolButton
                    label="Find & Replace (⌘H)"
                    testId="rte-find-replace"
                    active={findOpen && findMode === "replace"}
                    onClick={() => openFind("replace")}
                >
                    <Replace size={14} strokeWidth={1.7} />
                </ToolButton>
            </div>
            {findOpen && (
                <div
                    data-testid="rte-find-bar"
                    className="border-b border-[color:var(--charcoal)]/15 bg-[color:var(--cloud)] px-3 py-2 flex flex-wrap items-center gap-2"
                    onKeyDown={(e) => {
                        if (e.key === "Escape") {
                            e.preventDefault();
                            closeFind();
                        }
                    }}
                >
                    <Search size={13} strokeWidth={1.7} className="text-[color:var(--slate)]" />
                    <input
                        ref={findInputRef}
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                e.shiftKey ? gotoPrev() : gotoNext();
                            }
                        }}
                        placeholder="Find in article…"
                        data-testid="rte-find-input"
                        className="flex-1 min-w-[160px] max-w-[280px] px-2 py-1 border border-[color:var(--charcoal)]/25 font-body text-sm focus:outline-none focus:border-[color:var(--accent-violet)]"
                    />
                    <span
                        data-testid="rte-find-counter"
                        className="font-mono text-[11px] uppercase tracking-[0.18em] text-[color:var(--slate)] tabular-nums min-w-[60px]"
                    >
                        {query
                            ? totalMatches
                                ? `${activePosition} / ${totalMatches}`
                                : "0 / 0"
                            : "—"}
                    </span>
                    <button
                        type="button"
                        onClick={gotoPrev}
                        disabled={!totalMatches}
                        aria-label="Previous match"
                        title="Previous match (Shift+Enter)"
                        data-testid="rte-find-prev"
                        className="p-1 border border-[color:var(--charcoal)]/25 hover:bg-[color:var(--charcoal)]/5 disabled:opacity-30"
                    >
                        <ChevronUp size={14} strokeWidth={1.7} />
                    </button>
                    <button
                        type="button"
                        onClick={gotoNext}
                        disabled={!totalMatches}
                        aria-label="Next match"
                        title="Next match (Enter)"
                        data-testid="rte-find-next"
                        className="p-1 border border-[color:var(--charcoal)]/25 hover:bg-[color:var(--charcoal)]/5 disabled:opacity-30"
                    >
                        <ChevronDown size={14} strokeWidth={1.7} />
                    </button>
                    <label
                        title="Match case"
                        className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.18em] text-[color:var(--slate)] cursor-pointer select-none"
                    >
                        <input
                            type="checkbox"
                            checked={caseSensitive}
                            onChange={(e) => setCaseSensitive(e.target.checked)}
                            data-testid="rte-find-case"
                            className="h-3 w-3"
                        />
                        Aa
                    </label>
                    {findMode === "replace" && (
                        <>
                            <span className="w-px h-4 bg-[color:var(--charcoal)]/20 mx-1" />
                            <input
                                type="text"
                                value={replaceText}
                                onChange={(e) => setReplaceText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        onReplaceOne();
                                    }
                                }}
                                placeholder="Replace with…"
                                data-testid="rte-replace-input"
                                className="flex-1 min-w-[140px] max-w-[240px] px-2 py-1 border border-[color:var(--charcoal)]/25 font-body text-sm focus:outline-none focus:border-[color:var(--accent-violet)]"
                            />
                            <button
                                type="button"
                                onClick={onReplaceOne}
                                disabled={!totalMatches}
                                data-testid="rte-replace-one"
                                title="Replace current match (Enter in replace box)"
                                className="px-2 py-1 border border-[color:var(--charcoal)]/25 font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-[color:var(--charcoal)]/5 disabled:opacity-30"
                            >
                                Replace
                            </button>
                            <button
                                type="button"
                                onClick={onReplaceAll}
                                disabled={!totalMatches}
                                data-testid="rte-replace-all"
                                title="Replace every match"
                                className="px-2 py-1 border border-[color:var(--accent-violet)] text-white bg-[color:var(--accent-violet)] hover:bg-[color:var(--accent-violet-dark)] font-mono text-[11px] uppercase tracking-[0.18em] disabled:opacity-30"
                            >
                                All ({totalMatches})
                            </button>
                        </>
                    )}
                    <button
                        type="button"
                        onClick={closeFind}
                        aria-label="Close find"
                        title="Close (Esc)"
                        data-testid="rte-find-close"
                        className="p-1 border border-[color:var(--charcoal)]/25 hover:bg-[color:var(--charcoal)]/5 ml-auto"
                    >
                        <X size={13} strokeWidth={1.7} />
                    </button>
                </div>
            )}
            {/* Keep _tick referenced so eslint stays happy — it drives a
                render whenever plugin state changes even though the value
                itself is unused directly in the JSX. */}
            <span className="sr-only" aria-hidden data-tick={tick} />
            <EditorContent editor={editor} />
        </div>
    );
}
